// ============== PER-USER TELEGRAM CHANNEL CONNECTION ==============
// Users create a bot via @BotFather, add it as admin to their channel,
// then paste the bot token + @channelname here. No approval process.
import {
  useState, useEffect, axios, Button, Card, CardContent, CardHeader, CardTitle,
  Badge, Loader2, Input, API, toast,
} from "@/pages/shared";
import { Send } from "lucide-react";

export const TelegramPersonalSection = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [botToken, setBotToken] = useState("");
  const [chatId, setChatId] = useState("");

  const fetchStatus = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/social-connections/telegram/status`);
      setStatus(r.data);
    } catch (e) { setStatus({ connected: false }); }
    setLoading(false);
  };

  useEffect(() => { fetchStatus(); }, []);

  const handleConnect = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await axios.post(`${API}/social-connections/telegram/connect`, { bot_token: botToken, chat_id: chatId });
      toast.success(`Telegram connected — posting to ${r.data.chat_title}`);
      setShowForm(false); setBotToken(""); setChatId("");
      await fetchStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't connect Telegram");
    }
    setBusy(false);
  };

  const handleDisconnect = async () => {
    if (!confirm("Disconnect Telegram?")) return;
    setBusy(true);
    try {
      await axios.post(`${API}/social-connections/telegram/disconnect`);
      toast.success("Telegram disconnected");
      await fetchStatus();
    } catch (e) { toast.error("Couldn't disconnect"); }
    setBusy(false);
  };

  return (
    <Card className="glass" data-testid="settings-telegram-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Send className="w-5 h-5 text-sky-500" />
          Your Telegram channel
        </CardTitle>
        <p className="text-sm text-muted-foreground mt-1">
          Connect a Telegram channel and every scheduled auto-post also lands there (with the image when there is one).
        </p>
      </CardHeader>
      <CardContent>
        {loading ? (
          <Loader2 className="w-5 h-5 animate-spin" />
        ) : status?.connected ? (
          <div className="flex items-center justify-between gap-4 p-3 rounded-lg bg-white/5">
            <div>
              <p className="font-medium" data-testid="telegram-chat-title">{status.chat_title}</p>
              <p className="text-xs text-muted-foreground">via @{status.bot_username}</p>
            </div>
            <div className="flex items-center gap-2">
              <Badge className="bg-teal-500/20 text-teal-400" data-testid="telegram-badge">✓ Connected</Badge>
              <Button variant="outline" size="sm" onClick={handleDisconnect} disabled={busy} data-testid="telegram-disconnect-btn">Disconnect</Button>
            </div>
          </div>
        ) : showForm ? (
          <form onSubmit={handleConnect} className="space-y-3" data-testid="telegram-connect-form">
            <ol className="text-xs text-muted-foreground list-decimal pl-4 space-y-1">
              <li>Message <a href="https://t.me/BotFather" target="_blank" rel="noreferrer" className="text-violet-400 underline">@BotFather</a> → /newbot → copy the token</li>
              <li>Add your new bot as an <strong>admin</strong> of your channel</li>
              <li>Paste the token and your @channelname below</li>
            </ol>
            <Input value={botToken} onChange={(e) => setBotToken(e.target.value)} type="password" placeholder="Bot token (123456:ABC-...)" required data-testid="telegram-token-input" />
            <Input value={chatId} onChange={(e) => setChatId(e.target.value)} placeholder="@yourchannel or chat ID" required data-testid="telegram-chat-input" />
            <div className="flex gap-2">
              <Button type="submit" disabled={busy} className="bg-violet-600 hover:bg-violet-700" data-testid="telegram-submit-btn">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Connect Telegram"}
              </Button>
              <Button type="button" variant="ghost" onClick={() => setShowForm(false)}>Cancel</Button>
            </div>
          </form>
        ) : (
          <div className="flex items-center justify-between gap-4 p-3 rounded-lg bg-white/5">
            <div>
              <p className="font-medium">Telegram not connected</p>
              <p className="text-xs text-muted-foreground">Post every scheduled piece to your channel automatically.</p>
            </div>
            <Button onClick={() => setShowForm(true)} data-testid="telegram-connect-btn">Connect Telegram</Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
